import { useParams, Link } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { apiTenant } from "../lib/api"

interface Deadline {
  filing: string
  description: string
  forPeriod: string
  dueDate: string
  daysUntilDue: number
  amount: number | null
  status: "pending" | "due_soon" | "overdue" | "filed"
}

interface DashboardResponse {
  asOf: string
  workforce: { active: number; onLeave: number; terminatedThisMonth: number; total: number }
  pendingLeaveRequests: number
  currentRun: {
    id: string
    periodStart: string
    periodEnd: string
    status: string
    totalEmployees: number
    totalGross: number
    totalNet: number
  } | null
  deadlines: Deadline[]
  monthlyTrend: Array<{ month: string; gross: number; net: number; employees: number }>
}

const STATUS_COLOR: Record<Deadline["status"], string> = {
  overdue: "bg-red-100 text-red-700 border-red-200",
  due_soon: "bg-amber-100 text-amber-700 border-amber-200",
  pending: "bg-slate-100 text-slate-600 border-slate-200",
  filed: "bg-emerald-100 text-emerald-700 border-emerald-200",
}

const RUN_STATUS_COLOR: Record<string, string> = {
  draft: "bg-slate-200 text-slate-700",
  computed: "bg-blue-100 text-blue-700",
  approved: "bg-amber-100 text-amber-700",
  paid: "bg-emerald-100 text-emerald-700",
  reversed: "bg-red-100 text-red-700",
}

function fmtMoney(n: number): string {
  return n.toLocaleString("en-LK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function dueLabel(d: Deadline): string {
  if (d.daysUntilDue < 0) return `Overdue by ${Math.abs(d.daysUntilDue)} day${d.daysUntilDue === -1 ? "" : "s"}`
  if (d.daysUntilDue === 0) return "Due today"
  if (d.daysUntilDue === 1) return "Due tomorrow"
  return `Due in ${d.daysUntilDue} days`
}

export function PeopleDashboard() {
  const { slug = "" } = useParams<{ slug: string }>()
  const dash = useQuery({
    queryKey: ["people-dashboard", slug],
    enabled: !!slug,
    queryFn: () => apiTenant<DashboardResponse>(slug, "/dashboard"),
    refetchInterval: 60_000,
  })

  if (dash.isLoading) return <div className="text-sm text-slate-400">Loading…</div>
  if (dash.error) return <div className="text-sm text-red-600">{(dash.error as Error).message}</div>
  if (!dash.data) return null

  const d = dash.data
  const trendMax = Math.max(...d.monthlyTrend.map((m) => m.gross), 1)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <p className="text-xs text-slate-500 mt-1">
          Tenant <span className="font-mono">{slug}</span> · as of {new Date(d.asOf).toLocaleDateString("en-GB")}
        </p>
      </div>

      {/* Headline KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Active employees" value={String(d.workforce.active)} sub={d.workforce.onLeave > 0 ? `+${d.workforce.onLeave} on leave` : undefined} />
        <Stat label="Pending leave" value={String(d.pendingLeaveRequests)} sub={d.pendingLeaveRequests > 0 ? "awaiting decision" : "all caught up"} />
        <Stat
          label="Latest run"
          value={d.currentRun ? `${d.currentRun.totalEmployees} slips` : "—"}
          sub={d.currentRun ? `${d.currentRun.periodStart} → ${d.currentRun.periodEnd}` : "no runs yet"}
          pill={d.currentRun ? <span className={`text-[10px] px-1.5 py-0.5 rounded ${RUN_STATUS_COLOR[d.currentRun.status] ?? "bg-slate-100"}`}>{d.currentRun.status}</span> : undefined}
        />
        <Stat
          label="Latest gross"
          value={d.currentRun ? `LKR ${fmtMoney(d.currentRun.totalGross)}` : "—"}
          sub={d.currentRun ? `Net ${fmtMoney(d.currentRun.totalNet)}` : undefined}
          accent
        />
      </div>

      {/* Statutory deadlines */}
      <div className="bg-white border border-slate-200 rounded">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <div className="font-medium">Upcoming statutory filings</div>
          <Link to={`/t/${slug}/reports`} className="ml-auto text-xs text-brand-600 hover:text-brand-800">
            Generate filings →
          </Link>
        </div>
        {d.deadlines.length === 0 ? (
          <div className="p-6 text-sm text-slate-400 text-center">Nothing pressing.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Filing</th>
                <th className="text-left px-4 py-2 font-medium">For period</th>
                <th className="text-left px-4 py-2 font-medium">Due</th>
                <th className="text-right px-4 py-2 font-medium">Amount (LKR)</th>
                <th className="text-right px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {d.deadlines.map((dl) => (
                <tr key={`${dl.filing}-${dl.forPeriod}`} className="border-b border-slate-100 last:border-b-0">
                  <td className="px-4 py-2">
                    <div className="font-medium">{dl.filing}</div>
                    <div className="text-xs text-slate-500">{dl.description}</div>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{dl.forPeriod}</td>
                  <td className="px-4 py-2">
                    <div className="font-mono text-xs">{dl.dueDate}</div>
                    <div className="text-xs text-slate-500">{dueLabel(dl)}</div>
                  </td>
                  <td className="px-4 py-2 text-right font-mono">
                    {dl.amount != null ? fmtMoney(dl.amount) : <span className="text-slate-400 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <span className={`inline-block text-xs px-2 py-0.5 rounded border ${STATUS_COLOR[dl.status]}`}>
                      {dl.status === "due_soon" ? "due soon" : dl.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 12-month payroll cost trend */}
      <div className="bg-white border border-slate-200 rounded p-4">
        <div className="flex items-center mb-4">
          <div className="font-medium">12-month payroll cost</div>
          <Link to={`/t/${slug}/payroll`} className="ml-auto text-xs text-brand-600 hover:text-brand-800">
            View runs →
          </Link>
        </div>
        <div className="flex items-end gap-1.5 h-32 border-b border-slate-200">
          {d.monthlyTrend.map((m) => {
            const heightPct = (m.gross / trendMax) * 100
            return (
              <div key={m.month} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${m.month}: LKR ${fmtMoney(m.gross)}`}>
                <div
                  className={`w-full ${m.gross > 0 ? "bg-brand-500 hover:bg-brand-700" : "bg-slate-100"} rounded-t transition-colors`}
                  style={{ height: `${heightPct}%`, minHeight: m.gross > 0 ? "2px" : 0 }}
                />
              </div>
            )
          })}
        </div>
        <div className="flex gap-1.5 mt-1">
          {d.monthlyTrend.map((m) => (
            <div key={m.month} className="flex-1 text-[10px] text-slate-400 text-center font-mono truncate">
              {m.month.slice(5)}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function Stat(props: { label: string; value: string; sub?: string; accent?: boolean; pill?: React.ReactNode }) {
  return (
    <div className="bg-white border border-slate-200 rounded p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-2">
        {props.label}
        {props.pill}
      </div>
      <div className={`text-xl font-mono ${props.accent ? "font-semibold text-emerald-700" : "text-slate-900"}`}>
        {props.value}
      </div>
      {props.sub && <div className="text-[11px] text-slate-500 mt-0.5">{props.sub}</div>}
    </div>
  )
}
